import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Settings, X, User, Palette, LogOut, Ghost, Check, Lock, Image as ImageIcon, Sun, Moon, Heart } from "lucide-react";
import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";
import AvatarUpload from "./AvatarUpload";
import { validateUsername } from "../lib/username";

const THEME_KEY = "sm_theme_preference";

/** Applies a theme id to <html> by setting data-sm-theme.
 * Themes:
 *   - "dark" (default): the existing futuristic dark navy palette
 *   - "light": a bright UI with neon accents
 *   - "very-dark": deep blacks with stronger neon glow
 */
export function applyTheme(id) {
  const valid = ["dark", "light", "very-dark"];
  const final = valid.includes(id) ? id : "dark";
  try { document.documentElement.setAttribute("data-sm-theme", final); } catch {}
  return final;
}

export function loadTheme() {
  try {
    const t = localStorage.getItem(THEME_KEY);
    return applyTheme(t || "dark");
  } catch { return applyTheme("dark"); }
}

export default function SettingsModal({ open, onClose }) {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [section, setSection] = useState("account");

  if (!user) return null;
  const isGuest = !!user?.username?.startsWith?.("Guest_");

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="settings-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[120] flex items-center justify-center bg-[#02040D]/75 backdrop-blur-md p-4"
          onClick={onClose}
          data-testid="settings-modal"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 18 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0, y: 18 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="sm-card relative w-full max-w-2xl flex flex-col sm:flex-row overflow-hidden"
            style={{ borderColor: "rgba(59,130,246,0.55)", boxShadow: "0 0 50px rgba(59,130,246,0.30)" }}
          >
            <button
              onClick={onClose}
              className="absolute top-3 right-3 text-faint hover:text-white p-1 z-10"
              aria-label="Close"
              data-testid="settings-close"
            >
              <X size={18} />
            </button>

            <div className="sm:w-48 shrink-0 border-b sm:border-b-0 sm:border-r border-white/8 bg-white/[0.02] p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-9 h-9 grid place-items-center rounded-lg border border-blue-500/40 bg-blue-500/10">
                  <Settings size={16} className="text-blue" />
                </div>
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-soft">SETTINGS</div>
                  <div className="font-heading font-bold text-sm leading-tight">{user.username}</div>
                </div>
              </div>
              <nav className="flex sm:flex-col gap-1 overflow-x-auto sm:overflow-visible">
                <SideTab icon={<User size={14} />}       label="Account"  active={section === "account"} onClick={() => setSection("account")} testid="settings-tab-account" />
                <SideTab icon={<ImageIcon size={14} />}  label="Avatar"   active={section === "avatar"}  onClick={() => setSection("avatar")}  testid="settings-tab-avatar" />
                <SideTab icon={<Palette size={14} />}    label="Theme"    active={section === "theme"}   onClick={() => setSection("theme")}   testid="settings-tab-theme" />
                <SideTab icon={<Heart size={14} />}      label="Support"  active={section === "support"} onClick={() => setSection("support")} testid="settings-tab-support" />
                <SideTab icon={<LogOut size={14} />}     label="Sign out" active={section === "logout"}  onClick={() => setSection("logout")}  testid="settings-tab-logout" />
              </nav>
            </div>

            <div className="flex-1 p-6 sm:p-7 max-h-[80vh] overflow-y-auto">
              {section === "account" && (
                <AccountSection user={user} isGuest={isGuest} onUpdated={refresh} />
              )}
              {section === "avatar" && (
                <AvatarSection user={user} onUpdated={refresh} />
              )}
              {section === "theme" && (
                <ThemeSection />
              )}
              {section === "logout" && (
                <LogoutSection
                  onCancel={() => setSection("account")}
                  onConfirm={() => { logout(); onClose?.(); nav("/"); toast.success("Signed out"); }}
                />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SideTab({ icon, label, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-3 py-2.5 rounded-lg font-mono uppercase tracking-[0.2em] text-[11px] transition-colors whitespace-nowrap ${
        active
          ? "bg-blue-500/15 text-blue border border-blue-500/40"
          : "text-soft hover:text-white hover:bg-white/5 border border-transparent"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function AccountSection({ user, isGuest, onUpdated }) {
  const [name, setName] = useState(user.username);
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e?.preventDefault();
    const check = validateUsername(name);
    if (!check.ok) { toast.error(check.error); return; }
    const trimmed = check.value;
    if (trimmed === user.username) { toast.info("Username unchanged"); return; }
    setBusy(true);
    try {
      await apiClient.patch("/auth/username", { username: trimmed });
      toast.success("Username updated");
      await onUpdated?.();
    } catch (e) {
      toast.error(safeDetail(e) || "Could not update username");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Account</div>
      <h3 className="font-heading font-black text-2xl mt-1">Profile</h3>

      <form onSubmit={save} className="mt-6 space-y-4">
        <label className="block">
          <div className="font-mono text-[10px] tracking-[0.3em] text-soft mb-2 flex items-center gap-2">
            <User size={11} /> DISPLAY NAME
          </div>
          <div className="relative">
            <input
              className="sm-input pr-12"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isGuest}
              maxLength={20}
              data-testid="settings-username-input"
            />
            {isGuest && (
              <Lock size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-faint" />
            )}
          </div>
          <div className="mt-2 text-[11px] text-faint font-mono tracking-widest">
            3–20 chars · A-Z, 0-9, _ or -
          </div>
        </label>

        {isGuest ? (
          <div className="rounded-xl border border-purple-500/30 bg-purple-500/[0.06] p-4 flex items-start gap-3">
            <Ghost size={18} className="text-purple shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-heading font-bold text-white">You're a guest</div>
              <div className="text-soft mt-1">Create a full account to change your name and climb the global ladder.</div>
            </div>
          </div>
        ) : (
          <button
            type="submit"
            disabled={busy || name.trim() === user.username}
            className="sm-btn-primary px-6 py-3 flex items-center gap-2 disabled:opacity-50"
            data-testid="settings-username-save"
          >
            <Check size={14} /> {busy ? "SAVING…" : "SAVE CHANGES"}
          </button>
        )}
      </form>

      <div className="mt-8 pt-6 border-t border-white/8 grid grid-cols-2 gap-3 text-xs font-mono">
        <Stat label="ELO" value={user.elo} accent="text-blue" />
        <Stat label="Rank" value={user.rank || "—"} accent="text-purple" />
        <Stat label="Wins" value={user.wins ?? 0} accent="text-win" />
        <Stat label="Losses" value={user.losses ?? 0} accent="text-loss" />
      </div>
    </div>
  );
}

function AvatarSection({ user, onUpdated }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Avatar</div>
      <h3 className="font-heading font-black text-2xl mt-1">Profile Picture</h3>
      <div className="text-soft text-sm mt-2">
        Upload a custom image. Used across lobbies, leaderboards, and matches.
      </div>
      <div className="mt-6">
        <AvatarUpload user={user} onUpdated={onUpdated} />
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-lg border border-white/8 bg-white/[0.02] p-3">
      <div className="text-[10px] text-faint tracking-[0.25em] uppercase">{label}</div>
      <div className={`font-heading font-black text-xl ${accent}`}>{value}</div>
    </div>
  );
}

function ThemeSection() {
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem(THEME_KEY) || "dark"; } catch { return "dark"; }
  });
  const choose = (id) => {
    const final = applyTheme(id);
    setTheme(final);
    try { localStorage.setItem(THEME_KEY, final); } catch (_) { /* ignore */ }
    const labels = { dark: "Dark", light: "Light", "very-dark": "Very Dark" };
    toast.success(`Theme: ${labels[final]}`);
  };
  const options = [
    { id: "dark",      label: "Dark",      desc: "Default futuristic dark",        icon: <Moon size={18} className="text-blue" />,      preview: "linear-gradient(135deg, #0F172A, #0B1226 60%, #0A0F22)" },
    { id: "light",     label: "Light",     desc: "Bright UI, neon accents",         icon: <Sun size={18} className="text-amber-400" />,  preview: "linear-gradient(135deg, #F8FAFC, #E2E8F0)" },
    { id: "very-dark", label: "Very Dark", desc: "Deep blacks, stronger glow",      icon: <Moon size={18} className="text-purple" />,    preview: "linear-gradient(135deg, #000000, #050911 60%, #02040A)" },
  ];
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] text-blue uppercase">Theme</div>
      <h3 className="font-heading font-black text-2xl mt-1">Visual Settings</h3>
      <div className="text-soft text-sm mt-2">
        Choose your interface palette. Saved across sessions and applied app-wide.
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => choose(o.id)}
            className={`rounded-2xl p-4 text-left border transition-all ${
              theme === o.id ? "border-blue-500/70 bg-blue-500/[0.08]" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
            }`}
            data-testid={`settings-theme-${o.id}`}
          >
            <div className="w-full h-16 rounded-xl mb-3 border border-white/15" style={{ background: o.preview }} />
            <div className="flex items-center justify-between gap-2">
              <div className="font-heading font-bold">{o.label}</div>
              {o.icon}
            </div>
            <div className="text-faint font-mono text-[10px] tracking-widest mt-1">
              {theme === o.id ? "ACTIVE" : o.desc.toUpperCase()}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function LogoutSection({ onCancel, onConfirm }) {
  return (
    <div>
      <div className="font-mono text-[10px] tracking-[0.3em] text-loss uppercase">Sign out</div>
      <h3 className="font-heading font-black text-2xl mt-1">See you soon?</h3>
      <div className="text-soft text-sm mt-2">
        You'll need to sign back in to keep playing ranked matches.
      </div>

      <div className="mt-6 flex gap-3">
        <button onClick={onCancel} className="sm-btn-outline px-5 py-3" data-testid="settings-logout-cancel">CANCEL</button>
        <button
          onClick={onConfirm}
          className="px-5 py-3 rounded-xl font-heading font-bold uppercase tracking-[0.08em] text-white"
          style={{
            backgroundImage: "linear-gradient(135deg, #F43F5E 0%, #BE123C 100%)",
            boxShadow: "0 0 20px rgba(244,63,94,0.35)",
          }}
          data-testid="settings-logout-confirm"
        >
          <LogOut size={14} className="inline mr-2" /> SIGN OUT
        </button>
      </div>
    </div>
  );
}

function SupportSection({ onClose, nav }) {
  const handleDonate = () => {
    window.open("https://ko-fi.com/studymateranked", "_blank", "noopener,noreferrer");
  };

  const handleViewPage = () => {
    onClose?.();
    nav("/donate");
  };

  return (
    <div>
      <h2 className="font-heading font-black text-xl mb-2">Support StudyMateRanked</h2>
      <p className="text-sm text-soft leading-relaxed mb-6">
        Your support helps keep the servers running, enables new features, and makes StudyMateRanked better for everyone.
      </p>
      
      <div className="space-y-3">
        <button
          onClick={handleDonate}
          className="w-full px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 rounded-lg font-mono uppercase tracking-wider text-sm text-white transition-all shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 flex items-center justify-center gap-2"
          data-testid="settings-donate-kofi"
        >
          <Heart size={16} className="animate-pulse" />
          Support on Ko-fi
        </button>
        
        <button
          onClick={handleViewPage}
          className="w-full px-4 py-3 border border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/10 rounded-lg font-mono uppercase tracking-wider text-sm text-cyan-400 transition-all"
          data-testid="settings-view-donations"
        >
          View Donations Page
        </button>
      </div>

      <div className="mt-6 p-4 bg-white/[0.02] border border-white/8 rounded-lg">
        <p className="text-xs text-soft leading-relaxed">
          Every contribution helps cover server costs, development, and new features. Thank you for your support! 💙
        </p>
      </div>
    </div>
  );
}


// Safely flatten a backend error detail (which may be a string OR
// a list of pydantic-style validation dicts) into a single string.
function safeDetail(e) {
  const d = e?.response?.data?.detail;
  if (!d) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  }
  try { return JSON.stringify(d); } catch { return null; }
}
